import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
  type WorkerRequirementDomain,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { filterOutBackgroundCheckRequirements } from "@src/appV2/redesign/BackgroundChecks/filterOutBackgroundCheckRequirements";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "@src/appV2/redesign/BackgroundChecks/useGetRunBackgroundCheckOnBookingConfiguration";
import { type AxiosError } from "axios";

import { mergeWorkerRequirementWithIncludedResources } from "../mergeWorkerRequirementWithIncludedResources";
import { type WorkerRequirement } from "../types";
import { useGetWorkerRequirements } from "../useGetWorkerRequirements";

interface UseGetRequirementsForWorkplaceProps {
  qualifications: string[];
  workplaceId: string;
  domain?: WorkerRequirementDomain;
}

export function useGetRequirementsForWorkplace(
  props: UseGetRequirementsForWorkplaceProps,
  options: UseGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]> = {}
) {
  const { qualifications, workplaceId, domain } = props;

  const { isEnabled: isRunBackgroundCheckOnBookingEnabled } =
    useGetRunBackgroundCheckOnBookingConfiguration();

  return useGetWorkerRequirements(
    {
      filter: {
        workplaceIds: [workplaceId],
        qualifications,
        requiredForBooking: true,
        visibleToWorker: true,
        ...(domain ? { domains: [domain] } : {}),
      },
      include: [API_TYPES.requirements, API_TYPES.requirementMappings],
    },
    {
      select: (data) => {
        const mergedData = mergeWorkerRequirementWithIncludedResources(data);

        // ACT-3205: When run-background-check-on-booking is enabled, we hide "missing" UI for Checkr background checks.
        if (isRunBackgroundCheckOnBookingEnabled) {
          return filterOutBackgroundCheckRequirements(mergedData, {
            status: [WorkerRequirementStatus.MISSING],
          });
        }

        return mergedData;
      },
      ...options,
    }
  );
}
